import React, { useState, useEffect, useCallback, useMemo } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import {
  Box,
  Card,
  CardContent,
  Grid,
  TextField,
  Button,
  Typography,
  MenuItem,
  CircularProgress,
  Switch,
  FormControlLabel,
  Checkbox,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Autocomplete,
  Alert,
  Stack,
} from '@mui/material';
import { Save as SaveIcon, ArrowBack as BackIcon } from '@mui/icons-material';
import { toast } from 'react-toastify';
import api from '../../utils/api';
import InternationalPhoneField from '../../components/common/InternationalPhoneField';
import { previewProvisionedEmail } from '../../utils/provisioningPreview';
import {
  getPreviewDomainString,
  getEffectiveDomainId,
  initialPatternAndDomainIds,
} from '../../utils/provisionDomainSelection';
import { useAuth } from '../../context/AuthContext';
import UserGroupAssignmentPicker from './UserGroupAssignmentPicker';

function employeeBlock(c) {
  return c?.Employee || c?.employee || null;
}

const UserForm = () => {
  const navigate = useNavigate();
  const { hasPermission } = useAuth();
  const { id } = useParams();
  const isEdit = Boolean(id);
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [groups, setGroups] = useState([]);
  const [formData, setFormData] = useState({
    email: '',
    first_name: '',
    last_name: '',
    phone: '',
    is_admin: false,
    is_active: true,
    group_ids: [],
  });

  const [wizardOpen, setWizardOpen] = useState(!isEdit);
  const [manualOnly, setManualOnly] = useState(false);

  const [contactSearch, setContactSearch] = useState('');
  const [contactHits, setContactHits] = useState([]);
  const [contactLoading, setContactLoading] = useState(false);
  const [selectedContact, setSelectedContact] = useState(null);

  const [provDomains, setProvDomains] = useState([]);
  const [provPatterns, setProvPatterns] = useState([]);
  const [provDomainId, setProvDomainId] = useState('');
  const [provPatternId, setProvPatternId] = useState('');
  const [emailOverride, setEmailOverride] = useState('');
  const [updateContactEmail, setUpdateContactEmail] = useState(false);
  const [wizardGroups, setWizardGroups] = useState([]);
  const [wizardIsAdmin, setWizardIsAdmin] = useState(false);
  const [wizardSaving, setWizardSaving] = useState(false);
  const [wizardError, setWizardError] = useState('');

  useEffect(() => {
    fetchGroups();
    if (isEdit) fetchUser();
  }, [id]);

  const fetchProvRef = useCallback(async () => {
    try {
      const r = await api.get('/admin/provisioning-reference');
      const d = r.data?.data?.domains || [];
      const p = r.data?.data?.patterns || [];
      setProvDomains(d);
      setProvPatterns(p);
      const { patternId, domainId } = initialPatternAndDomainIds(p, d);
      setProvPatternId(patternId);
      setProvDomainId(domainId);
    } catch {
      setProvDomains([]);
      setProvPatterns([]);
    }
  }, []);

  useEffect(() => {
    if (!isEdit && wizardOpen && !manualOnly) {
      fetchProvRef();
    }
  }, [isEdit, wizardOpen, manualOnly, fetchProvRef]);

  const loadContacts = useCallback(async (q) => {
    setContactLoading(true);
    try {
      const r = await api.get('/users/provision-contacts', {
        params: { search: q || undefined, limit: 25 },
      });
      setContactHits(r.data?.data || []);
    } catch {
      setContactHits([]);
    } finally {
      setContactLoading(false);
    }
  }, []);

  useEffect(() => {
    if (!wizardOpen || manualOnly || isEdit) return undefined;
    const t = setTimeout(() => loadContacts(contactSearch), 300);
    return () => clearTimeout(t);
  }, [contactSearch, wizardOpen, manualOnly, isEdit, loadContacts]);

  const fetchGroups = async () => {
    try {
      const r = await api.get('/groups', { params: { limit: 500 } });
      setGroups(r.data.data || []);
    } catch (e) {
      /* ignore */
    }
  };

  const fetchUser = async () => {
    setLoading(true);
    try {
      const r = await api.get(`/users/${id}`);
      const user = r.data.data;
      const userGroups = user.groups || user.Groups || [];
      setFormData({ ...user, group_ids: userGroups.map((g) => g.id) });
    } catch (e) {
      toast.error('Failed to fetch');
      navigate('/admin/users');
    } finally {
      setLoading(false);
    }
  };

  const handleChange = (e) => setFormData((prev) => ({ ...prev, [e.target.name]: e.target.value }));

  const handleSubmit = async (e) => {
    e.preventDefault();
    setSaving(true);
    try {
      if (isEdit) await api.put(`/users/${id}`, formData);
      else await api.post('/users', formData);
      toast.success(`User ${isEdit ? 'updated' : 'created'}. ${!isEdit ? 'Login credentials sent via email.' : ''}`);
      navigate('/admin/users');
    } catch (error) {
      toast.error(error.response?.data?.message || 'Failed to save');
    } finally {
      setSaving(false);
    }
  };

  const selectedPattern = provPatterns.find((x) => x.id === provPatternId);
  const preview = useMemo(() => {
    if (!selectedPattern || !selectedContact) return emailOverride.trim() || '—';
    if (emailOverride.trim()) return emailOverride.trim();
    const domStr = getPreviewDomainString(provDomains, provDomainId, selectedPattern);
    if (!domStr) return '—';
    return previewProvisionedEmail(
      selectedPattern.pattern_template,
      selectedContact.first_name,
      selectedContact.last_name,
      domStr,
      selectedPattern
    );
  }, [selectedPattern, selectedContact, provDomains, provDomainId, emailOverride]);

  const emp = selectedContact ? employeeBlock(selectedContact) : null;
  const contactHasLogin = Boolean(emp?.user_id);
  const effectiveDomainId = getEffectiveDomainId(provDomains, provDomainId, selectedPattern);
  const canWizardSubmit =
    selectedContact &&
    !contactHasLogin &&
    provPatternId &&
    !wizardSaving &&
    Boolean(effectiveDomainId);

  const handleWizardPatternChange = (e) => {
    const pid = e.target.value;
    setProvPatternId(pid);
    const pat = provPatterns.find((x) => x.id === pid);
    const defId = pat?.default_domain_id || pat?.defaultDomain?.id;
    if (defId && provDomains.some((d) => d.id === defId)) {
      setProvDomainId(defId);
    }
  };

  const submitWizard = async () => {
    setWizardError('');
    if (!canWizardSubmit) return;
    const eff = getEffectiveDomainId(provDomains, provDomainId, selectedPattern);
    if (!eff) {
      setWizardError('Select a company domain, or set a default domain on the pattern.');
      return;
    }
    setWizardSaving(true);
    try {
      await api.post('/users/provision-from-contact', {
        contact_id: selectedContact.id,
        domain_id: eff,
        pattern_id: provPatternId,
        email_override: emailOverride.trim() || undefined,
        update_contact_email: updateContactEmail,
        is_admin: wizardIsAdmin,
        group_ids: wizardGroups,
      });
      toast.success('User created. Login credentials sent to the new address if mail is configured.');
      navigate('/admin/users');
    } catch (e) {
      setWizardError(e.response?.data?.message || 'Could not create user.');
    } finally {
      setWizardSaving(false);
    }
  };

  if (loading) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', py: 4 }}>
        <CircularProgress />
      </Box>
    );
  }

  const showManualForm = isEdit || manualOnly;

  return (
    <Box>
      <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, mb: 3 }}>
        <Button startIcon={<BackIcon />} onClick={() => navigate('/admin/users')}>
          Back
        </Button>
        <Typography variant="h4" sx={{ fontWeight: 700 }}>
          {isEdit ? 'Edit User' : 'New User'}
        </Typography>
      </Box>

      {!isEdit && (
        <Dialog open={wizardOpen && !manualOnly} onClose={() => navigate('/admin/users')} maxWidth="sm" fullWidth>
          <DialogTitle>Create user from contact</DialogTitle>
          <DialogContent>
            <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
              Choose a contact with type employee or contractor, then pick a user ID pattern. If that pattern has a
              default company domain, it is pre-filled and you can skip changing domain. The preview matches the saved
              template. If they have an HR profile without a login yet, the account is linked automatically.
            </Typography>
            {wizardError && (
              <Alert severity="error" sx={{ mb: 2 }}>
                {wizardError}
              </Alert>
            )}
            <Stack spacing={2} sx={{ mt: 1 }}>
              <Autocomplete
                options={contactHits}
                loading={contactLoading}
                value={selectedContact}
                onChange={(e, v) => {
                  setSelectedContact(v);
                  setEmailOverride('');
                }}
                onInputChange={(e, v) => setContactSearch(v)}
                getOptionLabel={(c) =>
                  `${c.first_name || ''} ${c.last_name || ''}`.trim() + (c.email ? ` · ${c.email}` : '')
                }
                getOptionDisabled={(c) => Boolean(employeeBlock(c)?.user_id)}
                renderOption={(props, c) => (
                  <li {...props} key={c.id}>
                    <Box>
                      <Typography variant="body2">
                        {c.first_name} {c.last_name}
                      </Typography>
                      <Typography variant="caption" color="text.secondary">
                        {c.email} · {c.contact_type}
                        {employeeBlock(c)?.user_id ? ' · already has login' : ''}
                      </Typography>
                    </Box>
                  </li>
                )}
                renderInput={(params) => (
                  <TextField
                    {...params}
                    label="Search employee / contractor contact"
                    placeholder="Name or email"
                    size="small"
                    InputProps={{
                      ...params.InputProps,
                      endAdornment: (
                        <>
                          {contactLoading ? <CircularProgress color="inherit" size={18} /> : null}
                          {params.InputProps.endAdornment}
                        </>
                      ),
                    }}
                  />
                )}
              />

              {selectedContact && contactHasLogin && (
                <Alert severity="warning">This contact already has a linked user on their HR profile.</Alert>
              )}

              {selectedContact && !contactHasLogin && (
                <>
                  {(!provDomains.length || !provPatterns.length) && (
                    <Alert severity="warning">
                      {!provDomains.length && 'Add company domains under Administrator → Company Domains. '}
                      {!provPatterns.length && 'Add user ID patterns under Administrator → User ID Pattern.'}
                    </Alert>
                  )}
                  <TextField
                    select
                    label="User ID pattern"
                    fullWidth
                    size="small"
                    value={provPatternId}
                    onChange={handleWizardPatternChange}
                    disabled={!provPatterns.length}
                  >
                    {provPatterns.map((p) => (
                      <MenuItem key={p.id} value={p.id}>
                        {p.name}
                        {(p.default_domain_id || p.defaultDomain?.id) ? ' · default domain' : ''}
                      </MenuItem>
                    ))}
                  </TextField>
                  <TextField
                    select
                    label="Company domain (optional if pattern has default)"
                    fullWidth
                    size="small"
                    value={provDomainId}
                    onChange={(e) => setProvDomainId(e.target.value)}
                    disabled={!provDomains.length}
                    helperText={
                      selectedPattern &&
                      (selectedPattern.default_domain_id || selectedPattern.defaultDomain?.id) &&
                      effectiveDomainId
                        ? 'Pre-filled from the pattern; change to use another domain.'
                        : undefined
                    }
                  >
                    {provDomains.map((d) => (
                      <MenuItem key={d.id} value={d.id}>
                        {d.company_name} — {d.domain}
                        {d.is_primary ? ' (primary)' : ''}
                      </MenuItem>
                    ))}
                  </TextField>
                  <Typography variant="caption" color="text.secondary">
                    Login email preview
                  </Typography>
                  <Typography variant="body2" sx={{ fontFamily: 'monospace', wordBreak: 'break-all' }}>
                    {preview}
                  </Typography>
                  <TextField
                    label="Override email (optional)"
                    fullWidth
                    size="small"
                    value={emailOverride}
                    onChange={(e) => setEmailOverride(e.target.value)}
                    helperText="Leave blank to use the pattern preview. Must be unique."
                  />
                  <FormControlLabel
                    control={
                      <Checkbox
                        checked={updateContactEmail}
                        onChange={(e) => setUpdateContactEmail(e.target.checked)}
                      />
                    }
                    label="Update contact email to this login address"
                  />
                  <UserGroupAssignmentPicker
                    groups={groups}
                    value={wizardGroups}
                    onChange={setWizardGroups}
                    disabled={wizardSaving}
                    canManageTemplates={hasPermission('users_modify')}
                  />
                  <FormControlLabel
                    control={<Switch checked={wizardIsAdmin} onChange={(e) => setWizardIsAdmin(e.target.checked)} />}
                    label="Administrator (full system access)"
                  />
                </>
              )}
            </Stack>
          </DialogContent>
          <DialogActions sx={{ flexWrap: 'wrap', gap: 1 }}>
            <Button
              onClick={() => {
                setManualOnly(true);
                setWizardOpen(false);
              }}
            >
              Enter email manually
            </Button>
            <Box sx={{ flexGrow: 1 }} />
            <Button onClick={() => navigate('/admin/users')}>Cancel</Button>
            <Button
              variant="contained"
              onClick={submitWizard}
              disabled={!canWizardSubmit || !provPatterns.length}
            >
              {wizardSaving ? <CircularProgress size={22} /> : 'Create user'}
            </Button>
          </DialogActions>
        </Dialog>
      )}

      {showManualForm && (
        <form onSubmit={handleSubmit}>
          {!isEdit && (
            <Button
              size="small"
              sx={{ mb: 2 }}
              onClick={() => {
                setManualOnly(false);
                setWizardOpen(true);
                setSelectedContact(null);
                setWizardError('');
              }}
            >
              Use contact, domain and pattern instead
            </Button>
          )}
          <Card sx={{ mb: 3 }}>
            <CardContent>
              <Typography variant="h6" sx={{ fontWeight: 600, mb: 3 }}>
                User Information
              </Typography>
              <Grid container spacing={3}>
                <Grid item xs={12} md={6}>
                  <TextField
                    fullWidth
                    label="First Name"
                    name="first_name"
                    value={formData.first_name}
                    onChange={handleChange}
                    required
                  />
                </Grid>
                <Grid item xs={12} md={6}>
                  <TextField
                    fullWidth
                    label="Last Name"
                    name="last_name"
                    value={formData.last_name}
                    onChange={handleChange}
                    required
                  />
                </Grid>
                <Grid item xs={12} md={6}>
                  <TextField
                    fullWidth
                    label="Email"
                    name="email"
                    type="email"
                    value={formData.email}
                    onChange={handleChange}
                    required
                    disabled={isEdit}
                  />
                </Grid>
                {isEdit ? (
                  <Grid item xs={12} md={6}>
                    <TextField
                      fullWidth
                      label="Reference"
                      value={formData.user_reference || ''}
                      InputProps={{ readOnly: true }}
                      helperText="USR-… assigned when the account was created (same format as other modules)"
                    />
                  </Grid>
                ) : null}
                <Grid item xs={12} md={isEdit ? 12 : 6}>
                  <InternationalPhoneField label="Phone" name="phone" value={formData.phone || ''} onChange={handleChange} />
                </Grid>
                <Grid item xs={12}>
                  <Typography variant="subtitle2" sx={{ mb: 1, fontWeight: 600 }}>
                    Groups
                  </Typography>
                  <UserGroupAssignmentPicker
                    groups={groups}
                    value={formData.group_ids || []}
                    onChange={(ids) => setFormData((prev) => ({ ...prev, group_ids: ids }))}
                    disabled={saving}
                    canManageTemplates={hasPermission('users_modify')}
                  />
                </Grid>
                <Grid item xs={12} md={6}>
                  <FormControlLabel
                    control={
                      <Switch
                        checked={formData.is_admin}
                        onChange={(e) => setFormData({ ...formData, is_admin: e.target.checked })}
                      />
                    }
                    label="Administrator (Full system access)"
                  />
                </Grid>
                <Grid item xs={12} md={6}>
                  <FormControlLabel
                    control={
                      <Switch
                        checked={formData.is_active}
                        onChange={(e) => setFormData({ ...formData, is_active: e.target.checked })}
                      />
                    }
                    label="Active"
                  />
                </Grid>
              </Grid>
            </CardContent>
          </Card>
          <Box sx={{ display: 'flex', justifyContent: 'flex-end', gap: 2 }}>
            <Button variant="outlined" onClick={() => navigate('/admin/users')}>
              Cancel
            </Button>
            <Button
              type="submit"
              variant="contained"
              startIcon={saving ? <CircularProgress size={20} /> : <SaveIcon />}
              disabled={saving}
              sx={{ background: 'linear-gradient(135deg, #6366f1 0%, #8b5cf6 100%)' }}
            >
              {saving ? 'Saving...' : 'Save User'}
            </Button>
          </Box>
        </form>
      )}
    </Box>
  );
};

export default UserForm;
